<template>
  <div v-loading="loading" class="qrcode relative inline-block" :style="wrapStyle">
    <canvas v-if="tag === 'canvas'" ref="wrapRef" @click="clickCode"></canvas>
    <img v-else ref="wrapRef" @click="clickCode" />

    <div
      v-if="disabled"
      class="qrcode--disabled absolute top-0 left-0 flex w-full h-full items-center justify-center"
      @click="disabledClick"
    >
      <div class="absolute top-[50%] left-[50%] font-bold">
        <el-icon size="30" color="var(--el-color-primary)">
          <RefreshRight />
        </el-icon>
        <div>{{ disabledText }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, unref, watch, nextTick, computed } from 'vue'
import { isString, cloneDeep } from '@pureadmin/utils'
import QRCode, { QRCodeRenderersOptions } from 'qrcode'
import { RefreshRight } from '@element-plus/icons-vue'

interface QrcodeLogo {
  src?: string
  logoSize?: number
  bgColor?: string
  borderSize?: number
  crossOrigin?: string
  borderRadius?: number
  logoRadius?: number
}

const props = defineProps({
  // img 或者 canvas,img不支持logo嵌套
  tag: {
    type: String,
    default: 'canvas',
    validator: (v: string) => ['canvas', 'img'].includes(v),
  },
  // 二维码内容
  text: {
    type: [String, Array],
    default: null,
  },
  // qrcode.js配置项
  options: {
    type: Object,
    default: (): QRCodeRenderersOptions => ({}),
  },
  // 宽度
  width: {
    type: Number,
    default: 200,
  },
  // logo
  logo: {
    type: [String, Object],
    default: (): QrcodeLogo | string => '',
  },
  // 是否过期
  disabled: {
    type: Boolean,
    default: false,
  },
  // 过期提示内容
  disabledText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['done', 'click', 'disabled-click'])

const { toCanvas, toDataURL } = QRCode
const loading = ref(true)
const wrapRef = ref<HTMLCanvasElement | HTMLImageElement | null>(null)
const renderText = computed(() => String(props.text))
const wrapStyle = computed(() => ({
  width: props.width + 'px',
  height: props.width + 'px',
}))

// 用于绘制圆角
const canvasRoundRect = (ctx: CanvasRenderingContext2D) => {
  return (x: number, y: number, w: number, h: number, r: number) => {
    const minSize = Math.min(w, h)
    if (r > minSize / 2) {
      r = minSize / 2
    }
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
    return ctx
  }
}

// 得到原QrCode的大小，以便缩放得到正确的QrCode大小
const getOriginWidth = async (content: string, options: QRCodeRenderersOptions) => {
  const _canvas = document.createElement('canvas')
  await toCanvas(_canvas, content, options)
  return _canvas.width
}

// 对于内容少的QrCode，增大容错率
const getErrorCorrectionLevel = (content: string) => {
  if (content.length > 36) {
    return 'M'
  } else if (content.length > 16) {
    return 'Q'
  } else {
    return 'H'
  }
}

const createLogoCode = async (canvasRef: HTMLCanvasElement) => {
  const canvasWidth = canvasRef.width
  const logoOptions: QrcodeLogo = Object.assign(
    {
      logoSize: 0.15,
      bgColor: '#ffffff',
      borderSize: 0.05,
      crossOrigin: 'anonymous',
      borderRadius: 8,
      logoRadius: 0,
    },
    isString(props.logo) ? {} : props.logo,
  )

  const {
    logoSize = 0.15,
    bgColor = '#ffffff',
    borderSize = 0.05,
    crossOrigin = 'anonymous',
    borderRadius = 8,
    logoRadius = 0,
  } = logoOptions

  const logoSrc = isString(props.logo) ? props.logo : props.logo.src
  const logoWidth = canvasWidth * logoSize
  const logoXY = (canvasWidth * (1 - logoSize)) / 2
  const logoBgWidth = canvasWidth * (logoSize + borderSize)
  const logoBgXY = (canvasWidth * (1 - logoSize - borderSize)) / 2

  const ctx = canvasRef.getContext('2d')
  if (!ctx) return

  // logo 底色
  canvasRoundRect(ctx)(logoBgXY, logoBgXY, logoBgWidth, logoBgWidth, borderRadius)
  ctx.fillStyle = bgColor
  ctx.fill()

  // logo
  const image = new Image()
  if (crossOrigin || logoRadius) {
    image.setAttribute('crossOrigin', crossOrigin)
  }
  image.src = logoSrc

  return new Promise((resolve) => {
    image.onload = () => {
      if (logoRadius) {
        const canvasImage = document.createElement('canvas')
        canvasImage.width = logoXY + logoWidth
        canvasImage.height = logoXY + logoWidth
        const imageCanvas = canvasImage.getContext('2d')
        if (!imageCanvas || !ctx) return
        imageCanvas.drawImage(image, logoXY, logoXY, logoWidth, logoWidth)
        canvasRoundRect(ctx)(logoXY, logoXY, logoWidth, logoWidth, logoRadius)
        const fillStyle = ctx.createPattern(canvasImage, 'no-repeat')
        if (fillStyle) {
          ctx.fillStyle = fillStyle
          ctx.fill()
        }
      } else {
        ctx.drawImage(image, logoXY, logoXY, logoWidth, logoWidth)
      }
      resolve(canvasRef.toDataURL())
    }
  })
}

const initQrcode = async () => {
  await nextTick()
  const options = cloneDeep(props.options || {})
  if (props.tag === 'canvas') {
    options.errorCorrectionLevel =
      options.errorCorrectionLevel || getErrorCorrectionLevel(unref(renderText))
    const _width = await getOriginWidth(unref(renderText), options)
    options.scale = props.width === 0 ? undefined : (props.width / _width) * 4

    const canvasRef = await toCanvas(
      unref(wrapRef) as HTMLCanvasElement,
      unref(renderText),
      options,
    )

    if (props.logo) {
      const url = await createLogoCode(canvasRef)
      emit('done', url)
      loading.value = false
    } else {
      emit('done', canvasRef.toDataURL())
      loading.value = false
    }
  } else {
    const url = await toDataURL(renderText.value, {
      errorCorrectionLevel: 'H',
      width: props.width,
      ...options,
    })
    if (unref(wrapRef)) {
      ;(unref(wrapRef) as HTMLImageElement).src = url
    }
    emit('done', url)
    loading.value = false
  }
}

const clickCode = () => {
  emit('click')
}

const disabledClick = () => {
  emit('disabled-click')
}

watch(
  () => renderText.value,
  (val) => {
    if (!val) return
    initQrcode()
  },
  {
    deep: true,
    immediate: true,
  },
)
</script>
<style lang="scss" scoped>
.qrcode {
  &--disabled {
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    user-select: none;
  }
}
</style>
